import type { Candidate } from './candidate'
import type { NutritionContext } from './inputs/snapshot'
import { redirectToNutritionAuthority } from './rulesets/shared'
import { EVIDENCE_PRECEDENCE, RULE_FAMILY_PRECEDENCE } from './thresholds'
import { RULE_IDS } from './version'

/** Maximum supporting actions alongside the one primary action (D-007). */
const MAX_SUPPORTING_ACTIONS = 2

/**
 * What the engine knows about how well the plan was followed (D-066).
 *
 * Three tiers, plus the absence of evidence:
 *
 *   `unknown`             nothing was recorded for the week
 *   `tooLowToJudge`       below ≥5 of 7 nutrition days AND ≥⅔ of sessions
 *   `sufficientToJudge`   at or above that, but below the higher tier
 *   `high`                ≥6 of 7 days AND all but one planned session
 *
 * The middle tier gates D-007's refusal to cut intake; the top tier is what
 * D-066 requires before *any* adjustment is proposed, and is one of the four
 * conditions for `high` confidence.
 *
 * Until Checkpoint 2.2 this was always `unknown`, because nothing collected it.
 * Checkpoints 2.0 and 2.1 now do, which is what makes 2.3's confidence mapping
 * implementable at all (D-060).
 */
export type AdherenceEvidence = 'unknown' | 'tooLowToJudge' | 'sufficientToJudge' | 'high'

/** True when adherence is good enough for the plan to have been tested. */
export function isJudgeable(adherence: AdherenceEvidence): boolean {
  return adherence === 'sufficientToJudge' || adherence === 'high'
}

/** The counts a week's adherence tier is decided from. */
export interface AdherenceObservation {
  readonly nutritionDaysOnPlan: number
  readonly trainingSessionsPlanned: number
  readonly trainingSessionsCompleted: number
}

/**
 * Map a week's recorded counts onto a tier (D-066).
 *
 * Both conditions must hold at each tier — days on plan **and** sessions
 * completed. Nutrition alone is not adherence to a plan that also has training
 * in it, and a perfect training week does not make an unfollowed diet judgeable.
 *
 * **A week with no sessions planned passes the session condition**, rather than
 * dividing by zero or failing. Someone taking a deliberate week off training has
 * not failed to follow their plan; they followed it.
 *
 * Pure, and reads its numbers from `thresholds.ts` — no literal appears here.
 */
export function classifyAdherence(
  observation: AdherenceObservation | undefined,
  thresholds: AdherenceTiers,
): AdherenceEvidence {
  if (observation === undefined) return 'unknown'

  const { nutritionDaysOnPlan: days, trainingSessionsPlanned: planned } = observation
  const completed = observation.trainingSessionsCompleted
  const missed = planned - completed
  const fraction = planned === 0 ? 1 : completed / planned

  if (
    days >= thresholds.nutritionDaysOnPlanToChange &&
    missed <= thresholds.plannedSessionsMissedAllowedToChange
  ) {
    return 'high'
  }
  if (
    days >= thresholds.nutritionDaysOnPlanToJudge &&
    fraction >= thresholds.plannedSessionFractionToJudge
  ) {
    return 'sufficientToJudge'
  }
  return 'tooLowToJudge'
}

interface AdherenceTiers {
  readonly nutritionDaysOnPlanToJudge: number
  readonly nutritionDaysOnPlanToChange: number
  readonly plannedSessionFractionToJudge: number
  readonly plannedSessionsMissedAllowedToChange: number
}

/**
 * Apply the invariants that must hold no matter what any ruleset proposes.
 *
 * These are enforced here, in one place, rather than trusted to every ruleset:
 *
 *  1. **Low or unknown adherence never yields a cut** (D-007). A candidate that
 *     would reduce intake or push activity up is replaced — not merely
 *     downgraded — when adherence cannot be judged. Implementation-plan §4 calls
 *     this a hard filter at prioritization "so no future ruleset can violate
 *     it", and that is exactly why it is not left to the rulesets.
 *  2. **The action points at whoever sets the numbers** (D-096). Under an
 *     external authority, "eat a little less" is an instruction Project Sola
 *     cannot carry out and the user cannot follow here.
 *  3. **Exactly one primary action, at most two supporting.**
 *
 * The order matters. The adherence filter may **replace** the candidate outright,
 * and the replacement is a `gatherMoreData` refusal that asks the user to change
 * nothing — so redirecting must come after it, or a refusal would be decorated
 * with instructions about where to make a change that is not being recommended.
 */
export function prioritize(
  candidate: Candidate,
  adherence: AdherenceEvidence,
  nutrition: NutritionContext,
): Candidate {
  const filtered = applyAdherenceFilter(candidate, adherence)
  const redirected = redirectToNutritionAuthority(filtered, nutrition)
  return capSupportingActions(redirected)
}

/**
 * Choose between competing candidates (D-066, Checkpoint 2.3).
 *
 * **Ordering, not weights.** The first family in `RULE_FAMILY_PRECEDENCE` that
 * fired supplies the primary action — safety and recovery, then adherence, then
 * phase progress. Ties inside a family break by `EVIDENCE_PRECEDENCE`, so a
 * waist-based candidate outranks a weight-based one (D-005). Both lists live in
 * `thresholds.ts` with the decision that authorised them; no number is invented
 * here, and per-family numeric weights were considered and rejected because
 * every one of them would have been made up and a score is hard to explain in
 * the rationale the user reads.
 *
 * **Everything not chosen is recorded, never discarded.** A rule that fired and
 * lost is exactly what "competing actions not selected and why" means in the
 * output contract, and dropping it would make the engine look more certain than
 * it was.
 *
 * The D-007 filter runs **after** selection, on the winner. Running it first
 * could let a filtered-away cut still win its family and suppress a real rival;
 * running it last means the winner is whatever the ordering chose, and the
 * refusal to cut intake applies to that.
 */
export function selectPrimary(
  candidates: readonly Candidate[],
  adherence: AdherenceEvidence,
  nutrition: NutritionContext,
): Candidate | undefined {
  if (candidates.length === 0) return undefined

  const ordered = [...candidates].sort(compareCandidates)
  const winner = ordered[0] as Candidate
  const losers = ordered.slice(1)

  const withAlternatives: Candidate = {
    ...winner,
    rejectedAlternatives: [
      ...winner.rejectedAlternatives,
      ...losers.map((loser) => ({
        action: loser.primaryAction,
        reason: reasonNotChosen(winner, loser),
      })),
    ],
  }

  return prioritize(withAlternatives, adherence, nutrition)
}

function compareCandidates(left: Candidate, right: Candidate): number {
  const byFamily =
    rank(RULE_FAMILY_PRECEDENCE, left.family) - rank(RULE_FAMILY_PRECEDENCE, right.family)
  if (byFamily !== 0) return byFamily
  return (
    rank(EVIDENCE_PRECEDENCE, left.leadingEvidence) -
    rank(EVIDENCE_PRECEDENCE, right.leadingEvidence)
  )
}

function rank<T extends string>(order: readonly T[], value: T): number {
  return order.indexOf(value)
}

/** Why the user is not being told to do this instead. */
function reasonNotChosen(winner: Candidate, loser: Candidate): string {
  if (winner.family !== loser.family) {
    return `${FAMILY_LABELS[winner.family]} comes before ${FAMILY_LABELS[loser.family]} when both apply in the same week.`
  }
  return `Both readings pointed at an action this week, and ${loser.leadingEvidence} is a weaker signal than ${winner.leadingEvidence} for judging body-composition change.`
}

const FAMILY_LABELS: Readonly<Record<Candidate['family'], string>> = {
  safetyAndRecovery: 'Safety and recovery',
  adherence: 'How closely the plan was followed',
  nutritionSourceConflict: 'A disagreement about your target rate',
  phaseProgress: 'Progress against the phase target',
}

function applyAdherenceFilter(candidate: Candidate, adherence: AdherenceEvidence): Candidate {
  if (!candidate.reducesIntakeOrActivity) return candidate
  if (isJudgeable(adherence)) return candidate

  /* The plan was never shown to have been followed, so it was never tested.
     Recommending less food on that basis would be reacting to missing data
     rather than to the plan. */
  return {
    category: 'gatherMoreData',
    primaryAction:
      'Keep this week as it is. Project Sola will not suggest eating less until it can see how closely the current plan was actually followed.',
    supportingActions: [],
    ruleId: RULE_IDS.fatLossOffTargetUnjudgeable,
    /* The refusal is an adherence finding, whatever family the candidate it
       replaced belonged to: what stops the action is how the week was followed. */
    family: 'adherence',
    leadingEvidence: 'weight',
    evidence: candidate.evidence,
    watchItems: candidate.watchItems,
    missingEvidence: [
      'How closely you followed your plan this week — no adherence was recorded for it',
      ...candidate.missingEvidence,
    ],
    rejectedAlternatives: [
      {
        action: candidate.primaryAction,
        reason:
          'It would reduce intake or increase activity, and adherence has not been measured, so there is no evidence the current plan was tested.',
      },
      ...candidate.rejectedAlternatives,
    ],
    reducesIntakeOrActivity: false,
  }
}

function capSupportingActions(candidate: Candidate): Candidate {
  if (candidate.supportingActions.length <= MAX_SUPPORTING_ACTIONS) return candidate
  return {
    ...candidate,
    supportingActions: candidate.supportingActions.slice(0, MAX_SUPPORTING_ACTIONS),
  }
}
